import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import {
  type VnnicGap,
  type VnnicReceipt,
  type VnnicReportStatus,
  type VnnicReportType,
  type VnnicSchemaStatus,
  type VnnicSyncErrorCode,
  type VnnicSyncStatus,
  type VnnicTransactionStatus,
} from '../../../api';
import { Badge, type Tone } from '../../../components/Badge';
import { Callout } from '../../../components/blocks';
import { EntityRef } from '../../../components/EntityRef';
import { formatDateTime } from '../../../lib/format';
import { EnumBadge, type EnumGroup } from '../ui';

type Meta = { label: string; tone: Tone };

export const VNNIC_BASE = '/compliance/vnnic';

// ---------------------------------------------------------------------------
// Status vocabularies

export const GAP: Record<VnnicGap, Meta & { description: string }> = {
  MISSING_DOSSIER: {
    label: 'Missing dossier',
    tone: 'crit',
    description: 'Managed domain with no dossier at all.',
  },
  INCOMPLETE_DOSSIER: {
    label: 'Incomplete dossier',
    tone: 'warn',
    description: 'Dossier exists but is not COMPLETE.',
  },
  VERIFICATION_NOT_VALID: {
    label: 'Verification not valid',
    tone: 'serious',
    description: 'Identity verification expired, failed or missing.',
  },
  SUBJECT_MISMATCH: {
    label: 'Subject mismatch',
    tone: 'serious',
    description: 'Dossier subject differs from the domain registrant.',
  },
  VNNIC_SYNC_MISSING: {
    label: 'Sync missing',
    tone: 'crit',
    description: '.vn name never submitted to VNNIC.',
  },
  VNNIC_SYNC_PENDING: {
    label: 'Sync pending',
    tone: 'pending',
    description: 'Submitted or queued, awaiting VNNIC acknowledgement.',
  },
  VNNIC_SYNC_REJECTED: {
    label: 'Sync rejected',
    tone: 'serious',
    description: 'VNNIC rejected or asked for a correction.',
  },
  VNNIC_SYNC_ERROR: {
    label: 'Sync error',
    tone: 'crit',
    description: 'Submission failed technically; the worker retries.',
  },
  REPORT_PROJECTION_MISSING: {
    label: 'Report projection missing',
    tone: 'warn',
    description: 'International name not projected into the reporting set.',
  },
  VNNIC_REGISTRY_MISSING: {
    label: 'Not on VNNIC list',
    tone: 'crit',
    description: 'Managed .vn name that VNNIC does not list among our domains.',
  },
};

export const GAP_ORDER = Object.keys(GAP) as VnnicGap[];

/** Where an operator resolves each gap. */
export function gapResolution(gap: VnnicGap): {
  to: string;
  key: 'sync' | 'reports' | 'dossiers' | 'registryList';
} {
  if (gap === 'VNNIC_REGISTRY_MISSING') return { to: `${VNNIC_BASE}/coverage#registry-list`, key: 'registryList' };
  if (gap.startsWith('VNNIC_SYNC_')) return { to: `${VNNIC_BASE}/dossier-sync`, key: 'sync' };
  if (gap === 'REPORT_PROJECTION_MISSING') return { to: `${VNNIC_BASE}/international-reports`, key: 'reports' };
  return { to: '/dossiers', key: 'dossiers' };
}

export const SYNC_STATUS: Record<VnnicSyncStatus, Meta> = {
  NOT_SYNCED: { label: 'Not synced', tone: 'crit' },
  SYNC_PENDING: { label: 'Sync pending', tone: 'pending' },
  QUEUED: { label: 'Queued', tone: 'pending' },
  SUBMITTING: { label: 'Submitting', tone: 'pending' },
  SUBMITTED: { label: 'Submitted', tone: 'info' },
  ACKNOWLEDGED: { label: 'Acknowledged', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  CORRECTION_REQUIRED: { label: 'Correction required', tone: 'serious' },
  RESUBMIT_PENDING: { label: 'Resubmit pending', tone: 'pending' },
  SYNC_ERROR: { label: 'Sync error', tone: 'crit' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral' },
};

export const SYNC_STATUS_ORDER = Object.keys(SYNC_STATUS) as VnnicSyncStatus[];

/** UNKNOWN means we sent it but never learned the answer: record the outcome, never retry blindly. */
export const TX_STATUS: Record<VnnicTransactionStatus, Meta> = {
  SUBMITTING: { label: 'Submitting', tone: 'pending' },
  SUBMITTED: { label: 'Submitted', tone: 'info' },
  ACKNOWLEDGED: { label: 'Acknowledged', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  CORRECTION_REQUIRED: { label: 'Correction required', tone: 'serious' },
  FAILED: { label: 'Failed', tone: 'crit' },
  UNKNOWN: { label: 'Unknown outcome', tone: 'serious' },
};

export const REPORT_STATUS: Record<VnnicReportStatus, Meta> = {
  GENERATED: { label: 'Generated', tone: 'pending' },
  VALIDATION_FAILED: { label: 'Validation failed', tone: 'crit' },
  VALIDATED: { label: 'Validated', tone: 'info' },
  EXPORTED: { label: 'Exported', tone: 'pending' },
  SUBMITTED: { label: 'Submitted', tone: 'pending' },
  ACKNOWLEDGED: { label: 'Acknowledged', tone: 'ok' },
  REJECTED: { label: 'Rejected', tone: 'crit' },
  SUPERSEDED: { label: 'Superseded', tone: 'neutral' },
};

export const REPORT_TYPE_LABEL: Record<VnnicReportType, string> = {
  '0805A': '0805A · annual',
  '0805B': '0805B · quarterly',
};

/** Report periods are Vietnamese calendar periods (UTC+7): "2025" or "2025 Q1". */
export function periodLabel(type: VnnicReportType, periodStart: string): string {
  const d = new Date(new Date(periodStart).getTime() + 7 * 3_600_000);
  if (Number.isNaN(d.getTime())) return periodStart;
  const year = d.getUTCFullYear();
  return type === '0805A' ? String(year) : `${year} Q${Math.floor(d.getUTCMonth() / 3) + 1}`;
}

/** Seconds elapsed since `iso`, or null when there is no timestamp. */
export function ageSeconds(iso: string | null | undefined, now: number = Date.now()): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.max(0, (now - t) / 1000);
}

// ---------------------------------------------------------------------------
// Unofficial schema warnings

export function SchemaBadge({ status, version }: { status: VnnicSchemaStatus; version?: string }) {
  const { t } = useTranslation('compliance');
  return status === 'OFFICIAL' ? (
    <Badge tone="ok">{version ? t('vnnic.unofficial.officialVersion', { version }) : t('vnnic.unofficial.official')}</Badge>
  ) : (
    <Badge tone="warn" title={t('vnnic.unofficial.badgeHint')}>
      {version ? t('vnnic.unofficial.badgeVersion', { version }) : t('vnnic.unofficial.badge')}
    </Badge>
  );
}

export function UnofficialSchemaNotice({ children }: { children?: ReactNode }) {
  const { t } = useTranslation('compliance');
  return (
    <Callout tone="warn" title={t('vnnic.unofficial.title')}>
      <p>{t('vnnic.unofficial.body')}</p>
      {children}
    </Callout>
  );
}

// ---------------------------------------------------------------------------
// Data and write helpers (shared across the console)

export { useRetained, useWrite, WriteError } from '../../../components/Write';

// ---------------------------------------------------------------------------
// Shared read-only blocks

export function ReceiptsTable({ receipts, statusMap, group = 'txStatus' }: { receipts: VnnicReceipt[]; statusMap: Record<string, Meta>; group?: EnumGroup }) {
  const { t } = useTranslation('compliance');
  if (receipts.length === 0) return <p className="text-sm text-muted-foreground">{t('vnnic.receipts.empty')}</p>;
  return (
    <div className="overflow-x-auto rounded-md border">
      <table className="w-full text-sm">
        <thead className="bg-muted/40 text-left text-xs font-semibold tracking-wide text-muted-foreground uppercase">
          <tr>
            <th scope="col" className="px-3 py-2">
              {t('vnnic.receipts.col.recorded')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('vnnic.receipts.col.outcome')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('vnnic.receipts.col.reference')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('vnnic.receipts.col.evidence')}
            </th>
            <th scope="col" className="px-3 py-2">
              {t('vnnic.receipts.col.note')}
            </th>
          </tr>
        </thead>
        <tbody className="divide-y">
          {receipts.map((r, i) => (
            <tr key={`${r.recorded_at}-${i}`} className="align-top">
              <td className="px-3 py-2 whitespace-nowrap">
                {formatDateTime(r.recorded_at)}
                {r.recorded_by_id ? (
                  <div className="text-xs text-muted-foreground">
                    {t('common.by')} <EntityRef label={r.recorded_by_display_name} id={r.recorded_by_id} />
                  </div>
                ) : null}
              </td>
              <td className="px-3 py-2">
                <EnumBadge group={group} value={r.outcome} tones={statusMap} />
              </td>
              <td className="px-3 py-2">{r.external_reference ? <code className="text-xs">{r.external_reference}</code> : <span className="text-muted-foreground">—</span>}</td>
              <td className="px-3 py-2 break-all">
                {r.evidence_object_key ? <code className="text-xs">{r.evidence_object_key}</code> : <span className="text-muted-foreground">—</span>}
              </td>
              <td className="px-3 py-2 break-words">{r.note ?? <span className="text-muted-foreground">—</span>}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

/**
 * Shows a payload exactly as the server returned it, collapsed by default.
 * `undefined` means the server withheld it (the caller lacks `regulatory_report.manage`).
 */
export function Payload({ label, value }: { label: string; value: unknown }) {
  const { t } = useTranslation('compliance');
  if (value === undefined) return <p className="text-xs text-muted-foreground">{t('vnnic.payload.withheld', { label })}</p>;
  if (value === null) return <p className="text-xs text-muted-foreground">{t('vnnic.payload.none', { label })}</p>;
  return (
    <details className="group rounded-md border bg-muted/30 text-sm">
      <summary className="cursor-pointer px-3 py-1.5 font-medium select-none hover:bg-muted/60">{label}</summary>
      <pre className="max-h-96 overflow-auto border-t p-3 font-mono text-xs leading-relaxed">{JSON.stringify(value, null, 2)}</pre>
    </details>
  );
}

export function EvidenceHelp() {
  const { t } = useTranslation('compliance');
  return <span className="text-xs text-muted-foreground">{t('vnnic.evidenceHelp')}</span>;
}

/**
 * What a dossier sync error code means: the platform's own codes have a meaning, any other
 * (VNNIC's, as recorded from its answer) reads as a code VNNIC returned. The code comes back as
 * text for secondary display.
 */
export function useSyncErrorMeaning() {
  const { t } = useTranslation('compliance');
  const lookup = t as unknown as (key: string, options: { defaultValue: string }) => string;
  return (value: VnnicSyncErrorCode): { meaning: string; code: string } => {
    const code = String(value);
    const other = t('vnnic.sync.otherCode');
    return { meaning: /^[A-Z0-9_]+$/.test(code) ? lookup(`enums.syncErrorCode.${code}`, { defaultValue: other }) : other, code };
  };
}

/** A dossier sync error code, by what it means; the code itself stays as secondary text. */
export function SyncErrorCode({ code }: { code: VnnicSyncErrorCode }) {
  const error = useSyncErrorMeaning()(code);
  return (
    <span className="inline-flex flex-wrap items-baseline gap-x-1.5">
      <span>{error.meaning}</span>
      <code className="text-xs text-muted-foreground">{error.code}</code>
    </span>
  );
}
